import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Zap, Trophy, Target, Flame, Brain, CheckCircle2, XCircle, Loader2, Crown, Medal, Award } from 'lucide-react';
import { toast } from 'sonner';
import FocusMode from '@/components/focus/FocusMode';
import AskAITutorButton from '@/components/focus/AskAITutorButton';
import FocusCelebration from '@/components/focus/FocusCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChallengerMode = ({ progress }) => {
  const navigate = useNavigate();
  const { stage, subject } = useParams();
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalStats, setFinalStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [stats, setStats] = useState({
    difficulty: 5,
    questionsAnswered: 0,
    correctAnswers: 0,
    streak: 0,
    maxStreak: 0,
    accuracy: 0
  });
  const [startTime, setStartTime] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadLeaderboard();
  }, [stage, subject]);

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/challenger/leaderboard/${stage}/${subject}`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const startSession = async () => {
    try {
      setLoading(true);
      setGameEnded(false);
      setFinalStats(null);
      setShowLeaderboard(false);
      
      const response = await axios.post(`${API}/challenger/start`, {
        stage,
        subject
      }, { headers });
      
      setSessionId(response.data.session_id);
      setStats(prev => ({ ...prev, difficulty: response.data.starting_difficulty }));
      toast.success('Challenger Mode Started! 🚀');
      
      // Get first question
      await getNextQuestion(response.data.session_id);
    } catch (error) {
      console.error('Error starting session:', error);
      if (error.response?.status === 403) {
        toast.error('Upgrade to access Challenger Mode');
        navigate('/subscription');
      } else {
        toast.error('Failed to start challenger session');
      }
    }
    setLoading(false);
  };

  const getNextQuestion = async (sid = sessionId) => {
    if (!sid) return;
    
    try {
      setLoading(true);
      setSelectedAnswer(null);
      setShowResult(false);
      setLastResult(null);
      
      const response = await axios.post(`${API}/challenger/question`, {
        session_id: sid
      }, { headers });
      
      setCurrentQuestion(response.data);
      setStats({
        difficulty: response.data.difficulty,
        questionsAnswered: response.data.questions_answered,
        correctAnswers: response.data.correct_answers,
        streak: response.data.current_streak,
        maxStreak: response.data.max_streak,
        accuracy: response.data.questions_answered > 0 
          ? Math.round((response.data.correct_answers / response.data.questions_answered) * 100)
          : 0
      });
      setStartTime(Date.now());
    } catch (error) {
      console.error('Error getting question:', error);
      toast.error('Failed to load question');
    }
    setLoading(false);
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null || !currentQuestion) return;
    
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/challenger/answer`, {
        session_id: sessionId,
        question_id: currentQuestion.question_id,
        selected_answer: selectedAnswer,
        time_taken: timeTaken
      }, { headers });
      
      setLastResult(response.data);
      setShowResult(true);
      
      setStats({
        difficulty: response.data.new_difficulty,
        questionsAnswered: response.data.questions_answered,
        correctAnswers: response.data.correct_answers,
        streak: response.data.current_streak,
        maxStreak: response.data.max_streak,
        accuracy: response.data.accuracy
      });
      
      if (response.data.correct) {
        toast.success(`Correct! 🎉 Difficulty: ${response.data.new_difficulty}/10`);
      } else {
        toast.error(`Incorrect. Difficulty: ${response.data.new_difficulty}/10`);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to submit answer');
    }
    setLoading(false);
  };

  const endSession = async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/challenger/end-session/${sessionId}`, {}, { headers });
      
      setFinalStats(response.data.final_stats);
      setGameEnded(true);
      toast.success(`Session ended! You ranked #${response.data.rank}! 🏆`);
      
      // Reload leaderboard
      await loadLeaderboard();
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error('Failed to end session');
    }
    setLoading(false);
  };

  const getDifficultyColor = (diff) => {
    if (diff <= 3) return 'bg-green-500';
    if (diff <= 5) return 'bg-yellow-500';
    if (diff <= 7) return 'bg-orange-500';
    if (diff <= 9) return 'bg-red-500';
    return 'bg-purple-600';
  };

  const getDifficultyLabel = (diff) => {
    if (diff <= 2) return 'Easy';
    if (diff <= 4) return 'Medium';
    if (diff <= 6) return 'Challenging';
    if (diff <= 8) return 'Hard';
    if (diff <= 9) return 'Very Hard';
    return 'Extreme';
  };

  const formatSubject = (s) => {
    return s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-sm font-bold text-gray-500">#{rank}</span>;
  };

  // Start screen
  if (!sessionId && !gameEnded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-700 hover:bg-gray-200"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>

          <Card className="shadow-xl bg-white border-gray-200" data-testid="challenger-start">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg text-center py-8">
              <div className="flex justify-center mb-4">
                <div className="bg-white/20 p-4 rounded-full">
                  <Zap className="h-12 w-12" />
                </div>
              </div>
              <CardTitle className="text-4xl font-bold mb-2">CHALLENGER MODE</CardTitle>
              <CardDescription className="text-white/90 text-lg">
                {formatSubject(subject)} - {stage.toUpperCase()}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8 bg-white">
              <Tabs defaultValue="play" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100">
                  <TabsTrigger value="play" className="data-[state=active]:bg-white data-[state=active]:text-gray-900">Play</TabsTrigger>
                  <TabsTrigger value="leaderboard" className="data-[state=active]:bg-white data-[state=active]:text-gray-900">Leaderboard</TabsTrigger>
                </TabsList>
                
                <TabsContent value="play" className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-xl text-center">
                      <Target className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="font-bold text-gray-800">Adaptive Difficulty</p>
                      <p className="text-sm text-gray-600">Questions get harder as you improve</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-xl text-center">
                      <Flame className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                      <p className="font-bold text-gray-800">Build Streaks</p>
                      <p className="text-sm text-gray-600">Consecutive correct answers boost your score</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl text-center">
                      <Trophy className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="font-bold text-gray-800">Compete</p>
                      <p className="text-sm text-gray-600">Climb the leaderboard</p>
                    </div>
                  </div>

                  <Button
                    onClick={startSession}
                    disabled={loading}
                    className="w-full h-14 text-xl font-bold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                    data-testid="start-challenger-btn"
                  >
                    {loading ? <Loader2 className="animate-spin mr-2" /> : <Zap className="mr-2" />}
                    START CHALLENGE
                  </Button>
                </TabsContent>
                
                <TabsContent value="leaderboard">
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {leaderboard.length > 0 ? (
                      leaderboard.map((entry, idx) => (
                        <div 
                          key={entry.id || idx}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            idx === 0 ? 'bg-yellow-50 border-yellow-300' :
                            idx === 1 ? 'bg-gray-50 border-gray-300' :
                            idx === 2 ? 'bg-amber-50 border-amber-300' :
                            'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 flex justify-center">
                              {getRankIcon(entry.rank)}
                            </div>
                            <div>
                              <p className="font-semibold">{entry.user_name}</p>
                              <p className="text-xs text-gray-500">
                                Difficulty {entry.max_difficulty} • {entry.accuracy}% accuracy
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-purple-600">{entry.score}</p>
                            <p className="text-xs text-gray-500">points</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Trophy className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No scores yet. Be the first!</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Game ended screen
  if (gameEnded && finalStats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl bg-white border-gray-200" data-testid="challenger-results">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg text-center py-8">
              <Trophy className="h-16 w-16 mx-auto mb-4" />
              <CardTitle className="text-3xl font-bold">Challenge Complete!</CardTitle>
            </CardHeader>
            <CardContent className="p-8 space-y-6 bg-white">
              <div className="text-center">
                <p className="text-6xl font-bold text-orange-600">{finalStats.score}</p>
                <p className="text-gray-600">Total Score</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl text-center">
                  <Brain className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-800">{finalStats.max_difficulty}</p>
                  <p className="text-xs text-gray-600">Max Difficulty</p>
                </div>
                <div className="bg-green-50 border border-green-200 p-4 rounded-xl text-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-800">{finalStats.correct_answers}</p>
                  <p className="text-xs text-gray-600">Correct</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-xl text-center">
                  <Flame className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-800">{finalStats.max_streak}</p>
                  <p className="text-xs text-gray-600">Best Streak</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl text-center">
                  <Target className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-800">{finalStats.accuracy}%</p>
                  <p className="text-xs text-gray-600">Accuracy</p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={startSession}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                >
                  <Zap className="mr-2 h-4 w-4" /> Play Again
                </Button>
                <Button
                  onClick={() => navigate(-1)}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700"
                >
                  Back to Subjects
                </Button>
              </div>

              {/* Mini Leaderboard */}
              <div className="mt-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800">
                  <Trophy className="h-5 w-5 text-yellow-500" /> Top Scores
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {leaderboard.slice(0, 5).map((entry, idx) => (
                    <div key={entry.id || idx} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded">
                      <div className="flex items-center gap-2">
                        {getRankIcon(entry.rank)}
                        <span className="font-medium text-gray-800">{entry.user_name}</span>
                      </div>
                      <span className="font-bold text-orange-600">{entry.score}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Active game screen — Seneca-style Focus Mode
  const formattedSubject = formatSubject(subject);
  const handleExit = () => {
    if (stats.questionsAnswered > 0) {
      if (window.confirm('Exit Challenger Series? Your unsaved progress will be lost — end & save first if you want to keep your score.')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  return (
    <FocusMode
      title={`${formattedSubject} · Challenger Series · Level ${stats.difficulty}/10 · ${getDifficultyLabel(stats.difficulty)}`}
      current={Math.max(stats.questionsAnswered + (showResult ? 0 : 1), 1)}
      total={Math.max(stats.questionsAnswered + 1, 20)}
      onExit={handleExit}
      showEstimate={false}
      tutorSlot={
        currentQuestion && (
          <AskAITutorButton
            endpoint="/api/ai-tutor/chat"
            context={{
              subject: formattedSubject,
              topic: currentQuestion.topic,
              question: currentQuestion.question,
              options: currentQuestion.options,
            }}
          />
        )
      }
    >
      {/* Quick stats strip */}
      <FocusCelebration correctCount={stats.correctAnswers} />
      <div className="flex flex-wrap items-center justify-between gap-2 pb-4 mb-4 border-b border-current/10 text-sm">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5"><Brain className="h-4 w-4 text-purple-600 dark:text-purple-400" /> <span className="font-semibold">{stats.questionsAnswered}</span> answered</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> <span className="font-semibold">{stats.correctAnswers}</span> correct</span>
          <span className="inline-flex items-center gap-1.5"><Flame className="h-4 w-4 text-orange-600 dark:text-orange-400" /> <span className="font-semibold">{stats.streak}</span> streak</span>
          <span className="inline-flex items-center gap-1.5"><Target className="h-4 w-4 text-sky-600 dark:text-sky-400" /> <span className="font-semibold">{stats.accuracy}%</span></span>
        </div>
        <Button
          onClick={endSession}
          variant="ghost"
          size="sm"
          disabled={loading || stats.questionsAnswered === 0}
          className="text-xs"
          data-testid="end-session-btn"
        >
          End &amp; Save Score
        </Button>
      </div>

      {/* Question Body */}
      {loading && !currentQuestion ? (
        <div className="py-12 text-center">
          <Loader2 className="h-10 w-10 animate-spin mx-auto opacity-70 mb-3" />
          <p className="opacity-70">Generating your next AI question…</p>
        </div>
      ) : currentQuestion ? (
        <div data-testid="question-card">
          {/* Topic + difficulty pills */}
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-700 dark:text-purple-300">{currentQuestion.topic}</span>
            <span className="px-2 py-0.5 rounded-full bg-current/10">Difficulty {currentQuestion.difficulty}/10</span>
          </div>

          <h2 className="leading-snug font-semibold mb-6" data-testid="question-text">
            {currentQuestion.question}
          </h2>

          <RadioGroup
            value={selectedAnswer !== null ? selectedAnswer.toString() : ''}
            onValueChange={(val) => !showResult && setSelectedAnswer(parseInt(val, 10))}
            disabled={showResult}
            className="space-y-3"
          >
            {currentQuestion.options.map((option, idx) => {
              const isSelected = selectedAnswer === idx;
              const isCorrect = showResult && lastResult
                ? idx === lastResult.correct_answer
                : idx === currentQuestion.correct_answer;
              return (
                <label
                  key={idx}
                  htmlFor={`opt-${idx}`}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-2 focus-within:ring-offset-transparent ${
                    showResult
                      ? isCorrect
                        ? 'bg-emerald-500/10 border-emerald-500'
                        : isSelected
                          ? 'bg-rose-500/10 border-rose-500'
                          : 'border-current/20 opacity-60'
                      : isSelected
                        ? 'bg-purple-500/10 border-purple-500'
                        : 'border-current/20 hover:border-purple-500 hover:bg-purple-500/5'
                  }`}
                  data-testid={`option-${idx}`}
                >
                  <RadioGroupItem value={idx.toString()} id={`opt-${idx}`} disabled={showResult} />
                  <span className="flex-1">{option}</span>
                  {showResult && isCorrect && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                  {showResult && isSelected && !isCorrect && <XCircle className="h-5 w-5 text-rose-600" />}
                </label>
              );
            })}
          </RadioGroup>

          {showResult && lastResult && (
            <div
              className={`mt-5 p-4 rounded-xl border ${lastResult.correct ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-rose-500/10 border-rose-500/50'}`}
              data-testid="answer-feedback"
            >
              <p className="font-semibold mb-2">
                {lastResult.correct ? '✓ Correct!' : '✗ Not quite'}
              </p>
              <p className="text-sm opacity-90 leading-relaxed">{currentQuestion.explanation}</p>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {!showResult ? (
              <Button
                onClick={submitAnswer}
                disabled={selectedAnswer === null || loading}
                className="flex-1 h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                data-testid="submit-answer-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" /> : null}
                Submit Answer
              </Button>
            ) : (
              <Button
                onClick={() => getNextQuestion()}
                disabled={loading}
                className="flex-1 h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                data-testid="next-question-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" /> : <Zap className="mr-2 h-5 w-5" />}
                Next Question
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </FocusMode>
  );
};

export default ChallengerMode;
